import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaPhone, FaEnvelope, FaUser, FaCommentDots } from 'react-icons/fa';
import { contactAPI } from '../services/api';
import './PopupContactForm.css';

const PopupContactForm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    // Check when the popup was last closed
    const lastClosedTime = localStorage.getItem('popupLastClosed');
    const now = Date.now();
    
    // If popup was closed less than 5 minutes ago, don't show it
    if (lastClosedTime && (now - parseInt(lastClosedTime)) < 5 * 60 * 1000) {
      return;
    }

    // Show popup after 15 seconds
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 15000);

    return () => clearTimeout(timer);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await contactAPI.submitContact(formData);
      if (response.data.success) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => {
          setIsOpen(false);
          setTimeout(() => setSubmitted(false), 500);
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    // Store the time when popup was closed
    localStorage.setItem('popupLastClosed', Date.now().toString());
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="popup-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />

          {/* Popup Modal */}
          <motion.div
            className="popup-contact-modal"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <button className="popup-close-btn" onClick={handleClose}>
              <FaTimes />
            </button>

            <div className="popup-header">
              <h2>Get in Touch!</h2>
              <p>We'd love to hear from you. Fill out the form below and we'll get back to you shortly.</p>
            </div>

            {!submitted ? (
              <form className="popup-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <div className="input-icon">
                    <FaUser />
                  </div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <div className="input-icon">
                    <FaEnvelope />
                  </div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <div className="input-icon">
                    <FaPhone />
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Your Phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <div className="input-icon">
                    <FaCommentDots />
                  </div>
                  <textarea
                    name="message"
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    required
                  ></textarea>
                </div>

                <button type="submit" className="popup-submit-btn" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Message'}
                </button>

                <div className="popup-contact-info">
                  <p>Or call us directly:</p>
                  <a href="tel:+919791177445" className="popup-phone-link">
                    <FaPhone /> +91 97911 77445
                  </a>
                </div>
              </form>
            ) : (
              <motion.div
                className="success-message"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="success-icon">✓</div>
                <h3>Thank You!</h3>
                <p>Your message has been sent successfully. We'll get back to you soon!</p>
              </motion.div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PopupContactForm;
